import Image from 'next/image'
import { useRouter } from 'next/router'

import Box from '@totallymoney/ui/components/Box'
import Flex from '@totallymoney/ui/components/Flex'
import Heading from '@totallymoney/ui/components/Heading'
import LinkButton from '@totallymoney/ui/components/LinkButton'
import Stack from '@totallymoney/ui/components/Stack'
import Text from '@totallymoney/ui/components/Text'
import InfoLabel from '@totallymoney/ui/productTable/components/InfoLabel'

import {
    CreditCardProductCategory,
    ScoredCreditCardProductsCategory,
} from '@appTypes/EligibilityApi.interface'

import NextLinkButton from '@components/NextLinkButton'

import useAppQueryState from '@hooks/useAppQueryState'

import partyPopper from '@images/party-popper.png'

import getEligibleContent from '@routes/ScoredTableRoute/components/ScoredTable/components/HighlightHero/components/HighlightHeroEligible/utils/getEligibleContent'
import HighlightHeroIneligible from '@routes/ScoredTableRoute/components/ScoredTable/components/HighlightHero/components/HighlightHeroIneligible'
import ScoredTableItem from '@routes/ScoredTableRoute/components/ScoredTable/components/ScoredTableItem'
import {
    HighlightCard,
    HighlightCardStatus,
} from '@routes/ScoredTableRoute/components/ScoredTable/utils/getHighlightCard'

import { getSanitisedCardName } from '@utils/getSanitisedCardName'

interface CreditCardsTableHighlightHeroProps {
    activeCategoryId: CreditCardProductCategory
    applicationUrl: string | null
    highlightedCreditCard: HighlightCard
    onApplyButtonPress: () => void
    onSeeMoreDetailsPress: () => void
    onShowGuaranteedOfferModal: () => void
    scoredCategories: ScoredCreditCardProductsCategory[]
}

const CreditCardsTableHighlightHero = ({
    applicationUrl,
    activeCategoryId,
    scoredCategories,
    highlightedCreditCard,
    onApplyButtonPress,
    onSeeMoreDetailsPress,
    onShowGuaranteedOfferModal,
}: CreditCardsTableHighlightHeroProps) => {
    const { getRouteWithAppState } = useAppQueryState()
    const { isReady } = useRouter()

    if (
        highlightedCreditCard.status === HighlightCardStatus.CardIneligible ||
        highlightedCreditCard.status === HighlightCardStatus.NoCard ||
        highlightedCreditCard.status === HighlightCardStatus.NoData
    ) {
        return (
            <HighlightHeroIneligible
                item={highlightedCreditCard.card}
                totalCreditCards={scoredCategories[0].totalCreditCards}
            />
        )
    }

    return (
        <Stack space="space20">
            {getEligibleContent(highlightedCreditCard.card) !== null ? (
                <Flex alignItems="center" gridColumnGap="space20">
                    <Image
                        src={partyPopper}
                        alt="Illustration of a party popper"
                        width={64}
                        height={64}
                    />
                    <Box>{getEligibleContent(highlightedCreditCard.card)}</Box>
                </Flex>
            ) : null}

            <ScoredTableItem
                item={highlightedCreditCard.card}
                category={activeCategoryId}
                isBestMatch={false}
                onShowGuaranteedOfferModal={onShowGuaranteedOfferModal}
                onShowRepresentativeExampleModal={onShowGuaranteedOfferModal}
                renderButtons={() => {
                    return (
                        <Flex
                            justifyContent="center"
                            flexDirection={['column', 'row-reverse', 'column']}
                            gridRowGap="space15"
                            gridColumnGap="space15"
                            width={['auto', 'auto', 212]}
                        >
                            <Box width="100%">
                                <LinkButton
                                    text="Apply"
                                    target="_blank"
                                    isExternalLink
                                    href={applicationUrl!}
                                    disabled={!isReady}
                                    variant="primary"
                                    onClick={event => {
                                        event.preventDefault()
                                        onApplyButtonPress()
                                    }}
                                />
                            </Box>
                            <Box width="100%">
                                <NextLinkButton
                                    text="More details"
                                    variant="secondaryOutline"
                                    to={getRouteWithAppState([
                                        'all-cards',
                                        'card',
                                        getSanitisedCardName(
                                            highlightedCreditCard.card?.name
                                        ),
                                    ])}
                                    onClick={onSeeMoreDetailsPress}
                                />
                            </Box>
                        </Flex>
                    )
                }}
            />

            <Box
                backgroundColor="indigo20"
                p="space10"
                borderRadius="borderRadius10"
            >
                {highlightedCreditCard.card.isExclusive ? (
                    <Text variant="80" weight="bold">
                        This is a TotallyMoney exclusive offer. Offers with
                        lower rates may be found below.
                    </Text>
                ) : (
                    <Text variant="80" weight="bold">
                        This is a promotional offer from TotallyMoney. Offers
                        with lower rates may be found below.
                    </Text>
                )}
            </Box>
        </Stack>
    )
}

export default CreditCardsTableHighlightHero
